import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import React, { FC, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import { InfoBox } from "../designUi";
import MedicTable from "./MedicTable";
import { MedicBoxIcon } from "../UIElements/svg-icons";
import { MedicContentProps } from "../../interface/medic";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { Pagination } from "../Pagination";
import { FilterMedicInputs } from "./FiltterInputs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { apiDeleteUser, apiSuspendUser } from "../../api/users";

const MedicContent: FC<MedicContentProps> = ({
  medicList,
  loading,
  stats,
  setFilterData,
  paginationState,
  setPaginationState,
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const [compare, setCompare] = useState(false);
  const { showNotification } = useNotificationService();
  const queryClient = useQueryClient();

  // Dialog states
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Suspend mutation
  const suspendMutation = useMutation({
    mutationFn: (params: { userId: string; suspended: boolean }) =>
      apiSuspendUser(params),
    onSuccess: (data) => {
      if (data?.status === "error") {
        showNotification(data?.msg || "Failed to update user status", "error");
        return;
      }
      showNotification(
        `User ${selectedUser?.suspended ? "unsuspended" : "suspended"} successfully`,
        "success",
      );
      setSuspendDialogOpen(false);
      setSelectedUser(null);
      queryClient.invalidateQueries(["medics"] as any);
      queryClient.invalidateQueries(["medic-stats"] as any);
    },
    onError: () => {
      showNotification("Failed to update user status", "error");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => apiDeleteUser(userId),
    onSuccess: (data) => {
      if (data?.status === "error") {
        showNotification(data?.msg || "Failed to delete user", "error");
        return;
      }
      showNotification("User deleted successfully", "success");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      queryClient.invalidateQueries(["medics"] as any);
      queryClient.invalidateQueries(["medic-stats"] as any);
    },
    onError: () => {
      showNotification("Failed to delete user", "error");
    },
  });

  const handleSuspendClick = (user: any) => {
    setSelectedUser(user);
    setSuspendDialogOpen(true);
  };

  const handleDeleteClick = (user: any) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleSuspendConfirm = () => {
    if (selectedUser) {
      suspendMutation.mutate({
        userId: selectedUser._id,
        suspended: !selectedUser.suspended, // Toggle the current state
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      deleteMutation.mutate(selectedUser._id);
    }
  };

  return (
    <Stack sx={{ padding: 2, marginTop: 70 }}>
      <Grid container>
        <Grid
          item
          xs={isSmall ? 12 : isMedium ? 6 : 4}
          sx={{ paddingX: isSmall ? 0 : 2, paddingY: 2 }}
        >
          <InfoBox
            title={"Total Medic"}
            value={stats.totalMedics ?? 0}
            icon={<MedicBoxIcon />}
          />
        </Grid>
        <Grid
          item
          xs={isSmall ? 12 : isMedium ? 6 : 4}
          sx={{ paddingX: isSmall ? 0 : 2, paddingY: 2 }}
        >
          <InfoBox
            title={"Vetted Medic"}
            value={stats.vettedMedics ?? 0}
            icon={<MedicBoxIcon />}
          />
        </Grid>
        <Grid
          item
          xs={isSmall ? 12 : isMedium ? 6 : 4}
          sx={{ paddingX: isSmall ? 0 : 2, paddingY: 2 }}
        >
          <InfoBox
            title={"Unvetted  Medic"}
            value={stats.unvettedMedics ?? 0}
            icon={<MedicBoxIcon />}
          />
        </Grid>
      </Grid>
      <Box sx={{ m: 2 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ mb: 1, px: 2 }}
        >
          <Typography
            variant="subtitle2"
            color={"neutral"}
            sx={{ fontWeight: 700, fontFamily: "Inter" }}
          >
            Medics
          </Typography>
          <Button onClick={() => setCompare(!compare)}>
            {compare ? <FilterAltOffIcon /> : <FilterAltIcon />}
          </Button>
        </Stack>
        <Box
          sx={{
            background: theme.palette.common.white,
            paddingY: 1,
            paddingX: 2,
            margin: isSmall ? 0 : 2,
            marginY: 3,
            borderRadius: 1,
          }}
        >
          {compare && (
            <FilterMedicInputs
              loading={loading}
              setFilterData={setFilterData}
            />
          )}
          <MedicTable
            data={medicList}
            loading={loading}
            onSuspend={handleSuspendClick}
            onDelete={handleDeleteClick}
          />
          <Pagination
            paginationState={paginationState}
            setPaginationState={setPaginationState}
          />
        </Box>
      </Box>

      {/* Suspend/Unsuspend Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onClose={() => setSuspendDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          {selectedUser?.suspended ? "Unsuspend" : "Suspend"} User
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {selectedUser?.suspended ? "unsuspend" : "suspend"}{" "}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            ?
            {!selectedUser?.suspended && (
              <Box sx={{ mt: 2, color: "warning.main" }}>
                This will prevent the user from accessing their account.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color={selectedUser?.suspended ? "success" : "warning"}
            onClick={handleSuspendConfirm}
            loading={suspendMutation.isPending}
          >
            {selectedUser?.suspended ? "Unsuspend" : "Suspend"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete{" "}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            ?
            <Box sx={{ mt: 2, color: "error.main", fontWeight: 600 }}>
              This action cannot be undone!
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            loading={deleteMutation.isPending}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MedicContent;
